/**
 * Ay kapanışı + evrensel dışa aktarım (katalog s. 24).
 *
 * Müşteri çalışma alanının "Month close" sekmesine çizilir (firm-workspace
 * mcRenderClose çağırır). Kilit veritabanında (transactions_period_lock):
 * kapalı döneme kimse yazamaz; yeniden açmak gerekçe ister ve denetime düşer.
 *
 * Dışa aktarım ÜLKE BAĞIMSIZ sütunlarla yapılır. Ülke biçimleri (CRA, SAF-T…)
 * bu sütunlar ve hesap kodu eşlemesi üzerine adaptör olarak sonra gelir.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

interface Period { period: string; status: string; closed_at: string | null; reopened_at: string | null; reopen_reason: string | null }
interface Row {
  id: string; date: string; type: string; category: string | null; amount: number; currency: string | null; note: string | null;
  net_amount: number | null; tax_rate: number | null; tax_amount: number | null; gross_amount: number | null;
  tax_code: string | null; document_no: string | null; counterparty_tax_id: string | null; fx_rate: number | null;
  source_document_id: string | null;
}

let ctx: { linkId: string; profileId: string; ready: boolean } | null = null;
let selected = '';

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function sb(): any { return legacy.sb; }

function months(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
function label(p: string): string {
  const [y, m] = p.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
function range(p: string): [string, string] {
  const [y, m] = p.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${p}-01`, `${p}-${String(last).padStart(2, '0')}`];
}

async function periodRows(): Promise<Row[]> {
  if (!ctx) return [];
  const [from, to] = range(selected);
  const r = await sb().from('transactions')
    .select('id,date,type,category,amount,currency,note,net_amount,tax_rate,tax_amount,gross_amount,tax_code,document_no,counterparty_tax_id,fx_rate,source_document_id')
    .eq('profile_id', ctx.profileId).gte('date', from).lte('date', to).order('date', { ascending: true }).limit(5000);
  return (r && r.data) || [];
}

async function codes(): Promise<Record<string, string>> {
  if (!ctx) return {};
  const r = await sb().from('category_account_codes').select('category,account_code').eq('profile_id', ctx.profileId);
  const map: Record<string, string> = {};
  for (const x of (r && r.data) || []) map[x.category] = x.account_code;
  return map;
}

async function render(): Promise<void> {
  const box = el('fw-close-actions');
  if (!box || !ctx) return;
  box.innerHTML = '…';
  const p = await sb().from('accounting_periods').select('period,status,closed_at,reopened_at,reopen_reason').eq('profile_id', ctx.profileId);
  const periods: Period[] = (p && p.data) || [];
  const current = periods.find((x) => x.period === selected);
  const closed = current?.status === 'closed';
  const rows = await periodRows();
  const map = await codes();
  const cats = Array.from(new Set(rows.map((r) => r.category || '').filter(Boolean).concat(Object.keys(map)))).sort();
  const income = rows.filter((r) => r.type === 'income').reduce((s, r) => s + Number(r.amount), 0);
  const expense = rows.filter((r) => r.type !== 'income').reduce((s, r) => s + Number(r.amount), 0);

  box.innerHTML = `<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:10px">`
    + `<select class="inp" style="flex:0 0 200px;padding:6px" onchange="mcSelect(this.value)">`
    + months(13).map((m) => `<option value="${m}"${m === selected ? ' selected' : ''}>${esc(label(m))}${periods.find((x) => x.period === m && x.status === 'closed') ? ' · closed' : ''}</option>`).join('')
    + `</select>`
    + (closed
      ? `<span style="font-size:11px;font-weight:700;color:var(--grn)">✓ Closed ${current?.closed_at ? '<span translate="no">' + esc(new Date(current.closed_at).toLocaleDateString()) + '</span>' : ''}</span>`
        + `<button class="btn bg bsm" onclick="mcReopen()">Reopen</button>`
      : `<button class="btn br bsm" onclick="mcClose()">Close ${esc(label(selected))}</button>`)
    + `</div>`
    + `<div style="font-size:12px;color:var(--t2);margin-bottom:10px"><span translate="no">${rows.length}</span> entries · income <b translate="no">${income.toFixed(2)}</b> · expense <b translate="no">${expense.toFixed(2)}</b>`
    + (current?.reopen_reason ? ` · last reopened: “${esc(current.reopen_reason)}”` : '') + `</div>`
    + `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px">`
    + `<button class="btn bg bsm" onclick="mcExport('csv')">⬇ Export CSV</button>`
    + `<button class="btn bg bsm" onclick="mcExport('xlsx')">⬇ Export XLSX</button></div>`
    + `<div class="set">Account codes</div>`
    + `<div style="font-size:11px;color:var(--t2);margin-bottom:8px">Map each category to your chart of accounts. The export uses these codes.</div>`
    + (cats.length
      ? cats.map((c) => `<div style="display:flex;gap:8px;align-items:center;margin-bottom:6px"><span style="flex:1;font-size:12px">${esc(c)}</span>`
        + `<input class="inp" data-mc-cat="${esc(c)}" maxlength="40" style="flex:0 0 140px;padding:6px;font-size:12px" placeholder="Code" value="${esc(map[c] ?? '')}"></div>`).join('')
        + `<button class="btn bg bsm" onclick="mcSaveCodes()">Save codes</button>`
      : '<div style="font-size:12px;color:var(--t3)">No categories in this period.</div>');
}

async function exportPeriod(kind: string): Promise<void> {
  if (!ctx) return;
  const rows = await periodRows();
  const map = await codes();
  const header = ['date', 'document_no', 'description', 'category', 'account_code', 'type', 'currency',
    'net_amount', 'tax_rate', 'tax_amount', 'gross_amount', 'amount', 'tax_code', 'counterparty_tax_id', 'fx_rate', 'source_document_id', 'entry_id'];
  const data = rows.map((r) => [r.date, r.document_no ?? '', r.note ?? '', r.category ?? '', map[r.category ?? ''] ?? '', r.type, r.currency ?? '',
    r.net_amount ?? '', r.tax_rate ?? '', r.tax_amount ?? '', r.gross_amount ?? '', r.amount, r.tax_code ?? '', r.counterparty_tax_id ?? '',
    r.fx_rate ?? '', r.source_document_id ?? '', r.id]);
  const name = `ledger-${selected}`;
  if (kind === 'xlsx' && (globalThis as any).XLSX) {
    const X = (globalThis as any).XLSX;
    const ws = X.utils.aoa_to_sheet([header, ...data]);
    const wb = X.utils.book_new();
    X.utils.book_append_sheet(wb, ws, selected);
    X.writeFile(wb, `${name}.xlsx`);
    return;
  }
  /* CSV: virgül/tırnak/satır sonu kaçışı; formül enjeksiyonuna karşı =,+,-,@ ile başlayan metin başına ' konur. */
  const cell = (v: unknown) => {
    let s = String(v ?? '');
    if (typeof v === 'string' && /^[=+\-@]/.test(s)) s = "'" + s;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...data].map((r) => r.map(cell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export default defineFeature({
  id: 'month-close',
  label: 'Ay kapanışı ve dışa aktarım',
  view: null,
  legacyGlobals: {
    mcRenderClose: (linkId: unknown, profileId: unknown, ready: unknown) => {
      ctx = { linkId: String(linkId), profileId: String(profileId), ready: ready === true };
      if (!selected) selected = months(1)[0];
      void render();
    },
    mcSelect: (p: unknown) => { selected = String(p); void render(); },
    mcClose: async () => {
      if (!ctx) return;
      if (!ctx.ready && !confirm('The checklist is not complete. Close this month anyway? Nobody can change its entries until it is reopened.')) return;
      const r = await sb().rpc('close_period', { p_profile_id: ctx.profileId, p_period: selected });
      legacy.toast(r?.data?.ok ? `${label(selected)} closed.` : (r?.data?.error ?? r?.error?.message ?? 'The month could not be closed.'));
      void render();
      (globalThis as any).fwReload?.();
    },
    mcReopen: async () => {
      if (!ctx) return;
      const reason = prompt('Why does this month need to be reopened? (at least 10 characters)');
      if (reason === null) return;
      const r = await sb().rpc('reopen_period', { p_profile_id: ctx.profileId, p_period: selected, p_reason: reason });
      legacy.toast(r?.data?.ok ? `${label(selected)} reopened.` : (r?.data?.error ?? 'The month could not be reopened.'));
      void render();
    },
    mcSaveCodes: async () => {
      if (!ctx) return;
      const map: Record<string, string> = {};
      document.querySelectorAll<HTMLInputElement>('[data-mc-cat]').forEach((i) => { map[i.dataset.mcCat ?? ''] = i.value.trim(); });
      const r = await sb().rpc('set_account_codes', { p_profile_id: ctx.profileId, p_codes: map });
      legacy.toast(r?.data?.ok ? 'Account codes saved.' : (r?.data?.error ?? 'The codes could not be saved.'));
    },
    mcExport: (kind: unknown) => exportPeriod(String(kind)),
  },
});
